/**
 * Is this actually the shipped app, or somebody's machine?
 *
 * NODE_ENV alone used to be the guard in instrumentation-client.ts, and the
 * comment above it claimed it stopped local errors polluting the production
 * project. It did not. NODE_ENV tells you a production BUILD is running, not
 * that it is running anywhere real, so a local `next build` served on a port
 * reports as production. On 2026-09-09 three issues arrived in the live ProFlow
 * Physiques project from HeadlessChrome on http://localhost:4800, one of which
 * Sentry attributed to the shift-demo repository. Users impacted: 0, because
 * there were no users.
 *
 * That is worse than noise. It makes the project untrustworthy: once some of the
 * errors are somebody's test run, none of the counts mean anything, and a real
 * one goes unnoticed among them.
 *
 * The check is deliberately narrow, because getting it wrong the other way costs
 * real monitoring. It blocks three things only, and everything else still reports.
 */
export interface SessionLocation {
  hostname: string;
  port: string;
}

export function isRealUserSession(
  location: SessionLocation | undefined,
  userAgent: string | undefined,
  webdriver: boolean | undefined,
): boolean {
  if (!location) return false;

  // The important one, and the reason it is phrased around the port rather than
  // the hostname: the native app really does run on localhost. Capacitor has no
  // `server` block in capacitor.config.ts, so the WebView serves from
  // capacitor://localhost on iOS and portless localhost on Android. A port means
  // a person started a server by hand.
  const isLoopback =
    location.hostname === "localhost" ||
    location.hostname === "127.0.0.1" ||
    location.hostname === "::1";
  if (isLoopback && location.port) return false;

  // Automation is never a user, even when pointed at the live domain.
  // navigator.webdriver is set by every driver; the pattern catches the headless
  // builds that do not set it.
  //
  // The `bot|crawler|spider` term carries two guards that a plain substring match
  // does not, and both are load bearing. `(?<!cu)` keeps CUBOT and cubot handsets
  // reporting: they are real Android phones whose model name sits in the user
  // agent, and a plain /bot/i silences every one of their owners. `\b` stops a
  // word that merely contains one of these from matching. This is the same
  // expression ownly-site has run in production since 2026-10-05, where it was
  // checked against Googlebot, Googlebot-Image, bingbot, Applebot, GPTBot,
  // SemrushBot, YandexBot and AhrefsBot (all refused) and against CUBOT, real
  // Chrome desktop and real iPhone Safari (all still reporting).
  if (webdriver) return false;
  if (
    /HeadlessChrome|Puppeteer|Playwright|(?<!cu)(?:bot|crawler|spider)\b/i.test(
      userAgent ?? "",
    )
  ) {
    return false;
  }

  return true;
}
